import { get, set } from 'lodash';
import { initialState } from '../constants/FavouritesConstants';
import ProjectionApiUtils from './ProjectionApiUtils';
import AccountsApiUtils from './AccountsApiUtils';
import * as PermissionsUtils from './PermissionsUtils';
import * as FavouritesUtils from './FavouritesUtils';
import * as FavouritesApiUtils from './FavouritesApiUtils';

export const setInitialState = () => {
  return {
    ...initialState,
    accountList: [...initialState.accountList],
    accountTypeColor: [...initialState.accountTypeColor],
    accountDetails: [...initialState.accountDetails],
    selectedAccount: { ...initialState.selectedAccount },
    permissions: { ...initialState.permissions },
  };
};

export const createColourTypeEntriesFromAccounts = (accountList = []) =>
  accountList.map((account, index) => ({
    accntId: account.id,
    accntClass: `account-${FavouritesUtils.wrapAccountIndex(index + 1)}`,
  }));

export const setShowIcon = ({ state, id, value }) => {
  return {
    ...state,
    accountList: FavouritesUtils.setShowIconOnAccount({
      accountList: state.accountList,
      id: id,
      value,
    }),
  };
};

export const getAccountByAccountNumberSortCode = ({
  accountList = [],
  sortCode,
  accountNumber,
}) => {
  const sortCodeFormatted = sortCode ? sortCode.replace(/-/g, '') : sortCode;
  return accountList.find(
    (a) => a.accountNumber === accountNumber && a.sortCode === sortCodeFormatted
  );
};

const makePotsCalls = ({ account, envConfig }) => {
  const isEnabled =
    envConfig.isSavingsPotsEnabled && envConfig.isFinancialStoryEnabled;
  if (isEnabled && PermissionsUtils.projectionsRequired(account)) {
    ProjectionApiUtils.getProjectionSummary(account.id);
  } else if (isEnabled && PermissionsUtils.hasPotsPermission(account)) {
    AccountsApiUtils.getPotsData(account.id);
  }
};

export const makePotsCallsForAccountList = ({ accountList = [], envConfig }) =>
  accountList.forEach((account) => makePotsCalls({ account, envConfig }));

export const handleDetailsSuccess = ({ state, accountDetails }) => {
  const { selectedAccount, accountList } = state;
  if (selectedAccount && selectedAccount.accountDetails) {
    selectedAccount.accountDetails.isLoaded = true;
  }

  return {
    ...state,
    accountList:
      accountList && accountList.find((a) => a.id === accountDetails.id)
        ? FavouritesUtils.updateFavouriteDetails({
            accountList,
            accountDetails,
          })
        : accountList,
    selectedAccount:
      selectedAccount && selectedAccount.id === accountDetails.id
        ? FavouritesUtils.updateSelectedAccountDetails({
            selectedAccount,
            accountDetails,
          })
        : selectedAccount,
    accountsLoaded: true,
  };
};

export const handleDetailsError = ({ id, accountDetails, state }) => {
  const { selectedAccount, accountList } = state;
  const accountDetailsId = get(accountDetails, 'id');
  if (!accountDetails) {
    return {
      ...state,
      selectedAccount: set(selectedAccount, 'accountDetails.error', true),
      accountsLoaded: true,
    };
  }

  return {
    ...state,
    accountList:
      accountList && accountList.find((a) => a.id === accountDetailsId)
        ? FavouritesUtils.updateFavouriteDetailsError({
            id,
            accountList,
            accountDetails,
          })
        : accountList,
    selectedAccount:
      selectedAccount && selectedAccount.id === accountDetailsId
        ? FavouritesUtils.updateSelectedAccountDetailsError({
            selectedAccount,
            accountDetails,
          })
        : selectedAccount,
    accountsLoaded: true,
  };
};

export const addFavourite = ({ state, account, isSpendingsEnabled }) => {
  const { accountList, permissions, accountTypeColor } = state;
  if (FavouritesUtils.canAddFavourite(accountList)) {
    return {
      ...state,
      updating: false,
      accountList: FavouritesUtils.addFavouriteToAccounts({
        accountList,
        account,
      }),
      permissions: PermissionsUtils.setPermissionsFromAccount({
        currentPermissions: permissions,
        account,
        isSpendingsEnabled,
      }),
      accountTypeColor: FavouritesUtils.appendColourTypeEntry({
        pickerColours: accountTypeColor,
        id: account.id,
      }),
    };
  }
  return state;
};

export const removeFavourite = ({ state, id }) => {
  return {
    ...state,
    updating: false,
    accountList: FavouritesUtils.removeFavouriteFromAccounts({
      accountList: state.accountList,
      id,
    }),
  };
};

export const reorderAccounts = ({ state, reorderedIds }) => {
  return {
    ...state,
    accountList: FavouritesUtils.reorderAccounts({
      reorderedIds,
      accountList: state.accountList,
    }),
  };
};

export const populateFavourites = ({
  state,
  accountList,
  totalNumberOfAccounts,
  isSpendingsEnabled,
}) => ({
  ...state,
  favouritesLoaded: true,
  loading: false,
  showError: false,
  accountList,
  totalNumberOfAccounts: totalNumberOfAccounts,
  permissions: PermissionsUtils.setPermissionsFromAccountList({
    accountList: accountList,
    isSpendingsEnabled,
    currentPermissions: state.permissions,
  }),
  accountTypeColor: createColourTypeEntriesFromAccounts(accountList),
});

export const addSelectedAccount = ({ state, account, isSpendingsEnabled }) => ({
  ...state,
  permissions: PermissionsUtils.setPermissionsFromAccount({
    currentPermissions: state.permissions,
    account,
    isSpendingsEnabled,
  }),
  selectedAccount: account,
});

const getOrderedIds = (accounts) =>
  accounts.map((a, index) => ({
    id: a.id,
    display_order: index + 1,
  }));

const buildAddRequest = (accounts, account) => ({
  accounts: getOrderedIds(accounts).concat([
    {
      id: account.id,
      display_order: accounts.length + 1,
    },
  ]),
});

const buildRemoveRequest = (accounts, id) => {
  const filteredAccounts = accounts.filter((acc) => acc.id !== id);
  return {
    accounts: getOrderedIds(filteredAccounts),
  };
};

export const sendAddFavouriteRequest = ({ accountList, account }) => {
  if (FavouritesUtils.canAddFavourite(accountList)) {
    const body = buildAddRequest(accountList, account);
    FavouritesApiUtils.addFavouriteAccount({ body, successArg: account });
  }
};

export const sendRemoveFavouriteRequest = ({ accountList, id }) => {
  const body = buildRemoveRequest(accountList, id);
  FavouritesApiUtils.removeFavouriteAccount({ body, successArg: id });
};

export const reorderFavouritesRequest = (reorderedIds) => {
  const body = {
    accounts: reorderedIds,
  };
  FavouritesApiUtils.reorderFavouriteAccounts({
    body,
    successArg: reorderedIds,
  });
};

export const setUpdating = (state) => ({
  ...state,
  updating: true,
  error: false,
});

export const renameAccounts = ({ state, data, accounts }) => {
  return {
    ...state,
    accountList: FavouritesUtils.performRenameAccounts({
      accounts,
      renamedAccounts: data.accounts,
    }),
  };
};

export const fetchQuickLinkPermissions = () => {
  PermissionsUtils.managePaymentPermissions.forEach((perm) =>
    AccountsApiUtils.getAccountsByCapability(perm)
  );
  AccountsApiUtils.getAccountsByCapability(PermissionsUtils.paymentCapability);
  AccountsApiUtils.getAccountsByCapability(
    PermissionsUtils.stmtPreferenceCapability
  );
};
